import {WalletBalance, WalletBilling} from './Wallet';

/**
 * Se a conta pode criar envio em massa no canal, e o que dizer quando não pode.
 *
 * Até 07/10/2026 as telas olhavam só o pacote (`credits` / `whatsapp_credits`): quem
 * migrou o SMS para a carteira (pacote zerado pelo drain) e todo cliente no WhatsApp
 * (cobrado em R$ desde 29/09) via "Você não possui créditos" e perdia o botão.
 * Quando o canal sai da carteira, quem decide é o saldo em R$; o backend reserva o
 * valor no hold e recusa se faltar.
 *
 * `unknown`: o `/wallet/billing` não respondeu e o pacote está zerado. Não dá para saber se
 * o canal sai da carteira (WhatsApp para todos, SMS migrado — os dois com pacote 0), então a
 * tela não trava: avisa e deixa o backend decidir (pacote ou hold) ao criar o envio.
 */
export type BroadcastFunds = 'ok' | 'no-credits' | 'no-wallet-balance' | 'unknown';

/**
 * A criação confere "créditos x contatos" do pacote? Com o billing: só quando o canal sai do
 * pacote. Sem o billing (fora do ar): pacote com saldo é sinal de canal no pacote — quem migrou
 * para a carteira tem o pacote zerado —, então a conta vale; com pacote 0 não trava (o backend
 * decide). Liberar todo mundo no billing nulo deixava o cliente do pacote criar um envio maior
 * que o pacote justamente no cenário degradado.
 *
 * `bucketCredits` nulo é pacote desconhecido (`/user-credits-info` falhou ou não respondeu):
 * sem o billing para dizer que o canal é da carteira, a conta vale — e a tela não libera sem
 * saber os créditos (falha fechada).
 *
 * WhatsApp com o billing fora do ar também confere o pacote quando há sobra nele: a cobrança do
 * WhatsApp pela carteira depende de `WALLET_WHATSAPP_ENABLED`, que pode estar desligada, e aí o
 * pacote é a cobrança. O falso positivo (cliente na carteira com sobra antiga barrado no cenário
 * degradado) é aceito: barrar é o lado seguro.
 */
export function checksPackageCredits(
  channel: 'sms' | 'whatsapp',
  billing: WalletBilling | null,
  bucketCredits: number | null,
): boolean {
  if (billing) {
    return billing[channel] !== 'wallet';
  }

  return bucketCredits === null || bucketCredits > 0;
}

/** A tela oferece criar o envio: com fundos, ou sem saber de onde sai a cobrança. */
export function allowsBroadcast(funds: BroadcastFunds | null): boolean {
  return funds === 'ok' || funds === 'unknown';
}

/** A tela de criação volta para a lista: só quando falta dinheiro com certeza. */
export function blocksBroadcast(funds: BroadcastFunds | null): boolean {
  return funds === 'no-credits' || funds === 'no-wallet-balance';
}

/** `bucketCredits` nulo: pacote desconhecido (a consulta falhou) — fora da carteira dá `unknown`. */
export function broadcastFunds(
  channel: 'sms' | 'whatsapp',
  bucketCredits: number | null,
  billing: WalletBilling | null,
  balance: WalletBalance | null,
): BroadcastFunds {
  if (billing && billing[channel] === 'wallet') {
    // Carteira fora do ar (sem saldo na mão) não trava a tela: o hold decide.
    if (!balance || balance.overdraft || balance.available_micros > 0) {
      return 'ok';
    }

    return 'no-wallet-balance';
  }

  if (bucketCredits === null) {
    return 'unknown';
  }

  if (bucketCredits > 0) {
    return 'ok';
  }

  return billing ? 'no-credits' : 'unknown';
}

/**
 * O 422 do backend que pede confirmação para agendar sem saldo para o envio inteiro (T.10,
 * 17tm466kqu7, regra do Bruno de 07/10): POST/PUT de `/broadcasts` e `/broadcast-calls`.
 * Nada foi gravado; o mesmo corpo com `confirm_partial: true` grava.
 *
 * `cost`/`available` vêm prontos para a tela ("R$ 34.500,00" na carteira, "230.000 créditos" no
 * pacote). `on_shortfall` diz o que acontece se, na hora do envio, faltar saldo:
 * - `partial_cancel` (carteira, SMS e ligação): sai parcial (~`estimated_partial_quantity`) e o
 *   restante é cancelado;
 * - `partial_wait` (ligação no pacote): saem as ligações que os créditos cobrem e o restante fica
 *   aguardando recarga;
 * - `cancel_all` (SMS no pacote, `partial: false`): o envio inteiro é cancelado.
 * O `message` traz a frase pronta, mas a tela decide pelos campos, nunca pelo texto.
 */
export type BalanceShortfall = 'partial_cancel' | 'partial_wait' | 'cancel_all';

export interface BalanceConfirmation {
  code: 'insufficient_balance_confirmation';
  message: string;
  product: 'sms' | 'call';
  billing: 'wallet' | 'package';
  cost: string;
  available: string;
  cost_micros: number | null;
  available_micros: number | null;
  quantity: number;
  estimated_partial_quantity: number;
  partial: boolean;
  on_shortfall: BalanceShortfall;
}

const BALANCE_CONFIRMATION_CODE = 'insufficient_balance_confirmation';

/**
 * O corpo do 422 de confirmação de saldo, ou null para qualquer outro erro. Reconhece pelo
 * `code`, nunca pelo texto: a frase do backend muda, o contrato não.
 */
export function balanceConfirmationOf(err: {status?: number; error?: any} | null | undefined): BalanceConfirmation | null {
  return err?.status === 422 && err.error?.code === BALANCE_CONFIRMATION_CODE
    ? err.error as BalanceConfirmation
    : null;
}

/** O corpo do agendamento, com `confirm_partial: true` só quando o cliente confirmou (T.10). */
export function withConfirmPartial<T extends object>(body: T, confirmPartial: boolean): T | T & {confirm_partial: true} {
  return confirmPartial ? {...body, confirm_partial: true} : body;
}
